import React from 'react';
import Icon from '../../../components/AppIcon';

const PricingCard = ({ 
  originalPrice = 0, 
  discountedPrice = 0, 
  savings = 0, 
  availableCount = 0,
  urgencyLevel = 'low',
  isSubsidized = false,
  subsidyAmount = 0
}) => {
  const discountPercentage = originalPrice > 0 ? Math.round(((originalPrice - discountedPrice) / originalPrice) * 100) : 0;
  
  const getUrgencyColor = () => {
    switch (urgencyLevel) {
      case 'high':
        return 'text-error';
      case 'medium':
        return 'text-warning';
      default:
        return 'text-success';
    }
  };

  const getUrgencyText = () => {
    switch (urgencyLevel) {
      case 'high':
        return '¡Últimas unidades!';
      case 'medium':
        return 'Disponibilidad limitada';
      default:
        return 'Disponible';
    }
  };

  const finalPrice = isSubsidized ? discountedPrice - subsidyAmount : discountedPrice;

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Urgency Indicator */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Icon 
            name={urgencyLevel === 'high' ? 'AlertTriangle' : 'Clock'} 
            size={16} 
            className={getUrgencyColor()}
          />
          <span className={`text-sm font-medium ${getUrgencyColor()}`}>
            {getUrgencyText()}
          </span>
        </div>
        
        {availableCount > 0 && (
          <div className="flex items-center space-x-1 bg-muted px-2 py-1 rounded-md">
            <Icon name="Package" size={14} color="var(--color-muted-foreground)" />
            <span className="text-sm text-muted-foreground">
              {availableCount} disponibles
            </span>
          </div>
        )}
      </div>
      {/* Pricing Information */}
      <div className="space-y-3">
        {/* Original Price */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Precio original:</span>
          <span className="text-lg text-muted-foreground line-through">
            ${originalPrice?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
          </span>
        </div>

        {/* Discounted Price */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-foreground">Precio con descuento:</span>
          <span className="text-xl font-semibold text-foreground">
            ${discountedPrice?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
          </span>
        </div>

        {/* University Subsidy */}
        {isSubsidized && (
          <div className="flex items-center justify-between bg-success/10 px-3 py-2 rounded-md">
            <div className="flex items-center space-x-2">
              <Icon name="GraduationCap" size={16} color="var(--color-success)" />
              <span className="text-sm text-success font-medium">Subsidio universitario:</span>
            </div>
            <span className="text-sm font-semibold text-success">
              -${subsidyAmount?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
            </span>
          </div>
        )}

        {/* Final Price */}
        {isSubsidized && (
          <div className="flex items-center justify-between border-t border-border pt-3">
            <span className="text-lg font-medium text-foreground">Precio final:</span>
            <span className="text-2xl font-bold text-success">
              ${finalPrice?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
            </span>
          </div>
        )}

        {/* Savings Summary */}
        <div className="bg-accent/10 px-4 py-3 rounded-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Icon name="TrendingDown" size={16} color="var(--color-accent)" />
              <span className="text-sm font-medium text-accent">
                Ahorras {discountPercentage}%
              </span>
            </div>
            <span className="text-lg font-semibold text-accent">
              ${(originalPrice - finalPrice)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
            </span>
          </div>
          
          {isSubsidized && (
            <div className="text-xs text-muted-foreground mt-1">
              Incluye ${subsidyAmount?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN de subsidio universitario
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PricingCard;